import { GridEditInputCell } from '@mui/x-data-grid';
import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import i18next from 'i18next';
import React, { useState } from 'react';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';

import { handleKeyDown } from '@commons/utils/dataGrid';
import Text, { ENUM_FONTS, ENUM_COLORS } from '@commons/Text';

import theme from '@theme';

import {
  NameCellContainer,
  RoundPicture,
  Name,
  NameHover,
  Sku,
  HeaderCellContainer,
} from './styledComponents';

const DEFAULT_PRODUCT_PICTURE = '/images/inpulse/product-photo-placeholder.svg';
const MAXIMUM_NAME_LENGTH = 22;

const commonCellStyle = {
  width: '60px',
  height: '40px',
  justifyContent: 'center',
  border: 1,
  borderRadius: 2,
  fontSize: 14,
};

export const renderNameCell = (name, { sku = null, withImg = false, imgSrc = null } = {}) => {
  const [displayBigger, setDisplayBigger] = useState(false);

  const img = imgSrc || DEFAULT_PRODUCT_PICTURE;

  return (
    <NameCellContainer>
      {withImg && <RoundPicture src={img} />}
      <Stack>
        <Name onMouseOut={() => setDisplayBigger(false)} onMouseOver={() => setDisplayBigger(true)}>
          {name}
        </Name>
        {displayBigger && name && name.length > MAXIMUM_NAME_LENGTH && (
          <NameHover offsetName onMouseOut={() => setDisplayBigger(false)}>
            {name}
          </NameHover>
        )}
        {sku && <Sku>{sku}</Sku>}
      </Stack>
    </NameCellContainer>
  );
};

export const renderCategoryCell = (name) => {
  const [displayBigger, setDisplayBigger] = useState(false);

  const formattedCategoryName = name || i18next.t('GENERAL.NONE_VALUE');

  return (
    <NameCellContainer>
      <Name onMouseOut={() => setDisplayBigger(false)} onMouseOver={() => setDisplayBigger(true)}>
        {formattedCategoryName}
      </Name>
      {displayBigger && name && name.length > MAXIMUM_NAME_LENGTH && (
        <NameHover offsetCategory onMouseOut={() => setDisplayBigger(false)}>
          {formattedCategoryName}
        </NameHover>
      )}
    </NameCellContainer>
  );
};

export const renderStandardHeader = (props, style = {}) => {
  const { colDef } = props;

  return (
    <NameCellContainer style={style}>
      <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>{colDef.headerName}</Text>
    </NameCellContainer>
  );
};

export const renderInputHeader = (props) => {
  const { colDef } = props;

  return (
    <NameCellContainer>
      <HeaderCellContainer>
        <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>{colDef.headerName}</Text>
        {colDef.headerSubTitle && (
          <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>{colDef.headerSubTitle}</Text>
        )}
      </HeaderCellContainer>
    </NameCellContainer>
  );
};

// We have to do this because the sx props doesn't work in this case
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    border: `1px solid ${theme.colors.infoRed}`,
    backgroundColor: theme.colors.greys.lightest,
  },
}));

export const renderInputCell = (props, rowAnimations = {}) => {
  const { isEditable, value, row } = props;
  const { id } = row;

  const formattedValue = value ? Math.round(value) : value;

  // Get animation state for this row
  const rowAnimation = rowAnimations[id];
  const isAnimating = rowAnimation?.isAnimating;
  const animationType = rowAnimation?.animationType;

  // Determine border color based on animation state
  let borderColor = theme.colors.greys.lighty; // Default grey

  if (isAnimating) {
    if (animationType === 'success') {
      borderColor = theme.colors.infoGreen; // Green for total = 100
    } else if (animationType === 'error') {
      borderColor = theme.colors.infoRed; // Red for total ≠ 100
    }
  }

  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      sx={{
        ...commonCellStyle,
        cursor: isEditable && 'pointer',
        transition: 'border-color 0.3s ease-in-out', // Smooth transition
        borderColor: isEditable ? borderColor : theme.colors.greys.lighty,
        backgroundColor: !isEditable && theme.colors.greys.lighty,
      }}
    >
      <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>{formattedValue}</Text>
    </Stack>
  );
};

export const renderEditInputCell = (props) => {
  const { error } = props;

  return (
    <StyledTooltip
      {...props}
      open={!!error}
      title={
        <Text color={ENUM_COLORS.INFO_RED}>
          {i18next.t('FORECAST.POS_PRODUCTION_PLANNING.CELL_INPUT_ERROR')}
        </Text>
      }
    >
      <GridEditInputCell
        {...props}
        sx={{
          ...commonCellStyle,
          color: error ? theme.colors.infoRed : 'inherit',
          borderColor: theme.colors.greys.lighty,
          textAlign: 'center',
          '& .MuiInputBase-input': {
            textAlign: 'center',
          },
        }}
        onFocus={(event) => event.target.select()}
        onKeyDown={handleKeyDown}
      />
    </StyledTooltip>
  );
};
